import * as React from "react";
import { Loader } from "lucide-react";

import { Button } from "../ui/button";

import { useDeleteRequest } from "@/hooks/useDeleteRequest";
import { useRequestGallery } from "./RequestGalleryContext";
import { Request } from "@/types/research.types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import RequestItem from "./RequestItem";

interface RequestListProps {
  onRequestSelect: (content: string, request?: Request) => void;
}

interface DeleteRequestDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

const DeleteRequestDialog: React.FC<DeleteRequestDialogProps> = ({
  isOpen,
  onOpenChange,
  onConfirm,
}) => (
  <Dialog open={isOpen} onOpenChange={onOpenChange}>
    <DialogContent className="p-4">
      <DialogHeader className="sm:text-center">
        <DialogTitle className="text-base font-medium text-center">Delete Request</DialogTitle>
        <DialogDescription className="text-center text-sm">
          Are you sure you want to delete this request? This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="flex-row gap-2 sm:justify-center">
        <Button variant="secondary" onClick={() => onOpenChange(false)} className="flex-1">
          Cancel
        </Button>
        <Button variant="destructive" onClick={onConfirm} className="flex-1">
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const filterRequests = (
  requests: Request[] | undefined,
  activeCategoryId: string | null,
  searchQuery: string
): Request[] => {
  if (!requests || activeCategoryId == null) return [];
  let filtered = requests.filter(
    (req) => activeCategoryId === "all" || req.category_id === activeCategoryId
  );
  if (searchQuery.trim()) {
    filtered = filtered.filter(
      (req) =>
        req.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.content.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }
  return [...filtered].reverse();
};

const RequestList: React.FC<RequestListProps> = ({ onRequestSelect }) => {
  const { searchQuery, activeCategoryId, setEditingRequest, setMode, requests, isLoadingRequests } =
    useRequestGallery();
  const deleteRequestMutation = useDeleteRequest();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [deletingRequestId, setDeletingRequestId] = React.useState<string | null>(null);

  const filteredRequests = React.useMemo(
    () => filterRequests(requests, activeCategoryId, searchQuery),
    [requests, activeCategoryId, searchQuery]
  );

  const handleDeleteRequest = (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation();
    const request = filteredRequests.find((r) => r.id === requestId);
    if (request?.user_id == null) return;
    setDeletingRequestId(requestId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteRequest = () => {
    if (deletingRequestId != null) {
      deleteRequestMutation.mutate(deletingRequestId);
      setIsDeleteDialogOpen(false);
      setDeletingRequestId(null);
    }
  };

  const handleEditClick = (e: React.MouseEvent, request: Request) => {
    e.stopPropagation();
    if (request.user_id == null) return;
    setEditingRequest(request);
    setMode("edit-request");
  };

  return (
    <>
      {isLoadingRequests ? (
        <div className="flex flex-col items-center justify-center h-full p-6">
          <Loader className="animate-spin" />
          <span className="ml-2">Loading requests...</span>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center text-neutral-500 text-sm">
          <p>{searchQuery ? "No requests found" : "No requests in this category"}</p>
        </div>
      ) : (
        filteredRequests.map((request) => (
          <RequestItem
            key={request.id}
            request={request}
            onClick={(content: string, req: Request) => onRequestSelect(content, req)}
            onEdit={handleEditClick}
            onDelete={handleDeleteRequest}
          />
        ))
      )}
      <DeleteRequestDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={confirmDeleteRequest}
      />
    </>
  );
};

export default RequestList;
